import { Check, ChevronDown } from 'lucide-react'
import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Subscription } from '@/lib/subscription'

interface SubscriptionSwitcherProps {
  current: Subscription
  available: Subscription[]
  onPick: (sub: Subscription) => void
}

export function SubscriptionSwitcher({ current, available, onPick }: SubscriptionSwitcherProps) {
  const sorted = useMemo(
    () => [...available].sort((a, b) => a.name.localeCompare(b.name)),
    [available],
  )

  if (available.length <= 1) {
    return (
      <span
        className="max-w-[220px] truncate text-xs text-muted-foreground"
        title={current.name}
      >
        {current.name}
      </span>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="-mr-2 h-7 max-w-[240px] gap-1 px-2 text-xs"
          aria-label={`Switch subscription (current: ${current.name})`}
        >
          <span className="truncate text-muted-foreground">{current.name}</span>
          <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {sorted.map((sub) => {
          const isCurrent = sub.id === current.id
          return (
            <DropdownMenuItem
              key={sub.id}
              onSelect={() => !isCurrent && onPick(sub)}
              className="flex items-center justify-between gap-3"
            >
              <span className={isCurrent ? 'font-medium' : ''}>{sub.name}</span>
              {isCurrent && <Check className="size-3.5 shrink-0" aria-hidden />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
